import {
  mdiAlertOutline,
  mdiBattery,
  mdiBattery10,
  mdiBattery20,
  mdiBattery30,
  mdiBattery40,
  mdiBattery50,
  mdiBattery60,
  mdiBattery70,
  mdiBattery80,
  mdiBattery90,
  mdiBatteryAlertVariantOutline,
  mdiCircle,
  mdiPowerPlug,
} from '@mdi/js';
import {
  DeviceStatusIslandBattery,
  DeviceStatusIslandItem,
  DeviceStatusIslandState,
} from './deviceStatusIslandTypes';

const STATE_SEVERITY: Record<DeviceStatusIslandState, number> = {
  online: 1,
  connected: 2,
  initializing: 3,
  measuring: 4,
  error: 5,
};

export interface DeviceStatusIslandStateInfo {
  labelKey: string;
  icon: string;
  color: string;
}

export const STATE_INFO: Record<DeviceStatusIslandState, DeviceStatusIslandStateInfo> = {
  online: { labelKey: 'StateOfUseEnum.Online', icon: mdiCircle, color: 'success' },
  connected: { labelKey: 'DeviceStatusIslandConnected', icon: mdiCircle, color: 'success' },
  initializing: { labelKey: 'StateOfUseEnum.Initializing', icon: mdiCircle, color: 'success' },
  measuring: { labelKey: 'StateOfUseEnum.Measuring', icon: mdiCircle, color: 'success' },
  error: { labelKey: 'StateOfUseEnum.DeviceError', icon: mdiAlertOutline, color: 'error' },
};

/** Most attention-worthy state across all items (undefined for an empty list). */
export function worstStateOf(items: DeviceStatusIslandItem[]): DeviceStatusIslandState | undefined {
  return items.reduce<DeviceStatusIslandState | undefined>(
    (worst, item) =>
      !worst || STATE_SEVERITY[item.state] > STATE_SEVERITY[worst] ? item.state : worst,
    undefined,
  );
}

/** Lowest real battery across all items; power-adapter items don't compete. */
export function lowestBatteryOf(
  items: DeviceStatusIslandItem[],
): DeviceStatusIslandBattery | undefined {
  let lowest: DeviceStatusIslandBattery | undefined;
  let adapter: DeviceStatusIslandBattery | undefined;
  for (const item of items) {
    if (!item.battery) continue;
    if (item.battery.percent === 'poweradapter') {
      adapter = item.battery;
    } else if (!lowest || item.battery.percent < (lowest.percent as number)) {
      lowest = item.battery;
    }
  }
  return lowest ?? adapter;
}

const BATTERY_ICONS: Record<number, string> = {
  0: mdiBatteryAlertVariantOutline,
  10: mdiBattery10,
  20: mdiBattery20,
  30: mdiBattery30,
  40: mdiBattery40,
  50: mdiBattery50,
  60: mdiBattery60,
  70: mdiBattery70,
  80: mdiBattery80,
  90: mdiBattery90,
  100: mdiBattery,
};

export interface DeviceStatusIslandBatteryDisplay {
  icon: string;
  color: string;
  sideways: boolean;
  label?: string;
}

export function batteryDisplayOf(
  battery: DeviceStatusIslandBattery,
): DeviceStatusIslandBatteryDisplay {
  if (battery.percent === 'poweradapter') {
    return { icon: mdiPowerPlug, color: '', sideways: false };
  }
  const percent = Math.max(0, Math.min(100, battery.percent));
  return {
    icon: BATTERY_ICONS[Math.trunc(percent / 10) * 10],
    color: percent > 40 ? 'success' : percent > 15 ? 'warning' : 'error',
    sideways: true,
    label: `${Math.round(percent)} %`,
  };
}
